import jsPDF from 'jspdf';

/**
 * Génère un PDF de fiche véhicule vierge à remplir à la main
 */
export const generateBlankVehiclePDF = (): void => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 15;

  const addHeader = () => {
    // En-tête entreprise
    doc.setFontSize(18);
    doc.setTextColor(0, 102, 204);
    doc.setFont('helvetica', 'bold');
    doc.text('TRUCKS 52 SARL', margin, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.setFont('helvetica', 'normal');
    doc.text("2 Place de l'Église, Recourt - 52140 Val-de-Meuse", margin, yPos);
    yPos += 4;
    doc.text('Tél: 06 08 24 85 29 | Email: trucks.52@orange.fr', margin, yPos);
    yPos += 4;
    doc.text('SIRET: 508 009 495 00013', margin, yPos);
    yPos += 8;

    // Titre du document
    doc.setFillColor(0, 102, 204);
    doc.rect(margin, yPos, contentWidth, 10, 'F');
    doc.setFontSize(14);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.text('FICHE VÉHICULE - NOUVELLE ANNONCE', pageWidth / 2, yPos + 7, { align: 'center' });
    yPos += 18;
  };

  const addSectionTitle = (title: string) => {
    if (yPos > 265) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(11);
    doc.setTextColor(0, 102, 204);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, yPos);
    yPos += 2;
    doc.setDrawColor(0, 102, 204);
    doc.line(margin, yPos, margin + 60, yPos);
    yPos += 6;
  };

  const addField = (label: string, width: number = contentWidth - 40) => {
    if (yPos > 275) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'normal');
    doc.text(`${label}:`, margin, yPos);
    
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setDrawColor(180);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(margin + labelWidth + 2, yPos, margin + labelWidth + 2 + width, yPos);
    doc.setLineDashPattern([], 0);
    yPos += 7;
  };

  const addDoubleField = (label1: string, label2: string) => {
    if (yPos > 275) {
      doc.addPage();
      yPos = 20;
    }
    const halfWidth = (contentWidth - 10) / 2;
    
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'normal');
    
    // Premier champ
    doc.text(`${label1}:`, margin, yPos);
    let labelWidth = doc.getTextWidth(`${label1}: `);
    doc.setDrawColor(180);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(margin + labelWidth + 2, yPos, margin + halfWidth - 5, yPos);
    
    // Deuxième champ
    const startX2 = margin + halfWidth + 5;
    doc.text(`${label2}:`, startX2, yPos);
    labelWidth = doc.getTextWidth(`${label2}: `);
    doc.line(startX2 + labelWidth + 2, yPos, pageWidth - margin, yPos);
    doc.setLineDashPattern([], 0);
    
    yPos += 7;
  };

  const addCheckboxGroup = (title: string, options: string[], columns: number = 3) => {
    if (yPos > 260) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'italic');
    doc.text(title, margin, yPos);
    yPos += 5;
    
    const colWidth = contentWidth / columns;
    let col = 0;
    let startY = yPos;
    
    doc.setFont('helvetica', 'normal');
    options.forEach((option) => {
      const x = margin + (col * colWidth);
      
      // Case à cocher
      doc.setDrawColor(100);
      doc.rect(x, yPos - 3, 3, 3);
      doc.text(option, x + 5, yPos);
      
      col++;
      if (col >= columns) {
        col = 0;
        yPos += 6;
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
      }
    });
    
    if (col !== 0) yPos += 6;
    yPos += 3;
  };

  const addTextArea = (label: string, lines: number = 3) => {
    if (yPos > 260) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.setFont('helvetica', 'normal');
    doc.text(`${label}:`, margin, yPos);
    yPos += 4;
    
    doc.setDrawColor(180);
    for (let i = 0; i < lines; i++) {
      doc.setLineDashPattern([1, 1], 0);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 6;
    }
    doc.setLineDashPattern([], 0);
    yPos += 2;
  };

  // === PAGE 1 ===
  addHeader();

  // INFORMATIONS PRINCIPALES
  addSectionTitle('INFORMATIONS PRINCIPALES');
  addField('Titre de l\'annonce', contentWidth - 50);
  addCheckboxGroup('Catégorie:', [
    'Camion', 'Utilitaire', 'Tracteur routier', 'Remorque', 
    'Semi-remorque', 'Bus/Car', 'Engin TP', 'Agricole', 'Autre'
  ], 3);
  addField('Marque', 60);
  addField('Modèle', 60);
  addDoubleField('Année', 'Kilométrage (km)');
  yPos += 3;

  // PRIX
  addSectionTitle('PRIX');
  addDoubleField('Prix HT (€)', 'Prix TTC (€)');
  addCheckboxGroup('Options:', ['Financement possible', 'Reprise acceptée'], 2);
  yPos += 3;

  // MOTORISATION
  addSectionTitle('MOTORISATION ET TRANSMISSION');
  addCheckboxGroup('Carburant:', ['Diesel', 'Essence', 'Électrique', 'Hybride', 'GNV', 'GPL'], 3);
  addCheckboxGroup('Transmission:', ['Manuelle', 'Automatique', 'Semi-automatique'], 3);
  addDoubleField('Puissance (ch)', 'Cylindrée (L)');
  yPos += 3;

  // CARACTÉRISTIQUES TECHNIQUES
  addSectionTitle('CARACTÉRISTIQUES TECHNIQUES');
  addDoubleField('PTAC (kg)', 'Charge utile (kg)');
  addField('Configuration essieux (ex: 4x2, 6x4)', 50);
  addField('Empattement (mm)', 40);
  addCheckboxGroup('Type de carrosserie:', [
    'Plateau', 'Benne', 'Fourgon', 'Frigorifique', 
    'Citerne', 'Porte-engins', 'Grue', 'Autre'
  ], 4);
  yPos += 3;

  // === PAGE 2 ===
  doc.addPage();
  yPos = 20;

  // NORMES ENVIRONNEMENTALES
  addSectionTitle('NORMES ENVIRONNEMENTALES');
  addCheckboxGroup('Norme Euro:', ['Euro 3', 'Euro 4', 'Euro 5', 'Euro 6', 'Euro 6d'], 5);
  addCheckboxGroup('Crit\'Air:', ['1 (Vert)', '2 (Jaune)', '3 (Orange)', '4 (Bordeaux)', '5 (Gris)', 'Exempté'], 3);
  yPos += 3;

  // ÉQUIPEMENTS
  addSectionTitle('ÉQUIPEMENTS');
  addCheckboxGroup('Confort:', [
    'Climatisation', 'Navigation GPS', 'Régulateur de vitesse',
    'Vitres électriques', 'Verrouillage central', 'Couchette'
  ], 3);
  addCheckboxGroup('Sécurité:', [
    'ABS', 'ESP', 'Caméra de recul', 'Radar de recul',
    'Freinage d\'urgence', 'Aide au maintien de voie'
  ], 3);
  addCheckboxGroup('Équipements spécifiques:', [
    'Hayon élévateur', 'Grue auxiliaire', 'Benne basculante',
    'Groupe frigorifique', 'Compresseur', 'Attelage'
  ], 3);
  addTextArea('Autres équipements', 2);
  yPos += 3;

  // PNEUMATIQUES
  addSectionTitle('PNEUMATIQUES');
  addDoubleField('Dimension avant', 'Dimension arrière');
  addCheckboxGroup('État des pneus:', ['Neuf', 'Très bon', 'Bon', 'Usé', 'À remplacer'], 5);
  yPos += 3;

  // ENTRETIEN
  addSectionTitle('ENTRETIEN');
  addDoubleField('Date dernier entretien', 'Prochain entretien (km)');
  addDoubleField('Contrôle technique (date)', 'Valide jusqu\'au');
  yPos += 3;

  // === PAGE 3 ===
  doc.addPage();
  yPos = 20;

  // HISTORIQUE ET ÉTAT
  addSectionTitle('HISTORIQUE ET ÉTAT');
  addField('Nombre de propriétaires précédents', 30);
  addCheckboxGroup('Historique d\'accident:', ['Non', 'Oui (détailler ci-dessous)'], 2);
  addTextArea('Détails accidents / réparations', 2);
  addCheckboxGroup('État général:', ['Excellent', 'Très bon', 'Bon', 'Correct', 'À réviser'], 5);
  addCheckboxGroup('État extérieur:', ['Excellent', 'Très bon', 'Bon', 'Correct', 'Endommagé'], 5);
  addCheckboxGroup('État intérieur:', ['Excellent', 'Très bon', 'Bon', 'Correct', 'Usé'], 5);
  yPos += 3;

  // DISPONIBILITÉ
  addSectionTitle('DISPONIBILITÉ ET LOCALISATION');
  addField('Disponible à partir du', 40);
  addField('Lieu de stationnement', 60);
  yPos += 3;

  // PHOTOS ET VIDÉOS
  addSectionTitle('PHOTOS ET VIDÉOS');
  addCheckboxGroup('Photos à prendre:', [
    'Vue avant', 'Vue arrière', 'Vue latérale gauche', 'Vue latérale droite',
    'Tableau de bord', 'Compartiment moteur', 'Espace de chargement', 'Pneus',
    'Documents (CT, carnet)', 'Défauts éventuels'
  ], 2);
  addField('Lien vidéo (optionnel)', 60);
  yPos += 3;

  // NOTES
  addSectionTitle('NOTES ET OBSERVATIONS');
  addTextArea('Notes complémentaires', 6);

  // Pied de page sur toutes les pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Trucks 52 SARL - Fiche nouvelle annonce - Page ${i}/${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );
    
    // Date d'impression
    const today = new Date().toLocaleDateString('fr-FR');
    doc.text(`Imprimé le ${today}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
  }

  // Télécharger le PDF
  doc.save('Trucks52-Fiche-Nouvelle-Annonce.pdf');
};
